import { useEffect, useState } from 'react';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import { FullStory } from '@fullstory/browser';
import * as Sentry from '@sentry/react';
import { get } from 'aws-amplify/api';
import { AuthUser, fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { type IImport } from '@/types/import';
import { AppContext, AppContextType, type MailersSession, type Person } from './lib/contextLib';
import { Router } from './Router';
import { theme } from './theme';

export default function App({ retrieveUrl }: { retrieveUrl: string }) {
  const [isAuthenticated, userHasAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [currentUser, setCurrentUser] = useState<Person>({} as Person);
  const [imports, setImports] = useState<IImport[]>([]);
  const [mailersSession, setMailersSession] = useState<MailersSession | null>(null);
  const [isMailersSessionLoading, setIsMailersSessionLoading] = useState(true);

  useEffect(() => {
    onLoad();
  }, [isAuthenticated]);

  async function currentAuthenticatedUser() {
    try {
      const gotCurrentUser: AuthUser = await getCurrentUser();

      const userAttributes = await fetchUserAttributes();
      FullStory('setIdentity', {
        uid: userAttributes.email,
        properties: { ...gotCurrentUser, ...userAttributes },
      });

      Sentry.setUser({
        id: gotCurrentUser.userId,
        email: userAttributes.email,
      });

      setCurrentUser({ ...gotCurrentUser, ...userAttributes } as Person);
      return { ...gotCurrentUser, ...userAttributes } as Person;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async function currentSession() {
    try {
      const { accessToken } = (await fetchAuthSession({ forceRefresh: true })).tokens ?? {};
      return accessToken;
    } catch (err) {
      console.log(err);
    }
  }

  async function onLoad() {
    try {
      const session = await currentSession();
      if (session) {
        await currentAuthenticatedUser();
        await loadMailersSession();
        userHasAuthenticated(true);
      } else {
        setMailersSession(null);
        setIsMailersSessionLoading(false);
      }
    } catch (e) {
      if (e !== 'No current user') {
        console.error(e);
      }
      setIsMailersSessionLoading(false);
    }

    setIsAuthenticating(false);
  }

  async function loadMailersSession() {
    try {
      const response = await get({
        apiName: 'MailersApi',
        path: '/mailers/me',
        options: {
          retryStrategy: {
            strategy: 'no-retry',
          },
        },
      }).response;

      const session = (await response.body.json()) as unknown as MailersSession;
      setMailersSession(session);
    } catch (error: any) {
      setMailersSession(null);
      console.log('mailers session unavailable', error);
    } finally {
      setIsMailersSessionLoading(false);
    }
  }

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <ModalsProvider>
        <AppContext.Provider
          value={
            {
              retrieveUrl,
              isAuthenticating,
              isAuthenticated,
              userHasAuthenticated,
              currentUser,
              setCurrentUser,
              imports,
              setImports,
              mailersSession,
              setMailersSession,
              isMailersSessionLoading,
            } as AppContextType
          }
        >
          <Router />
        </AppContext.Provider>
      </ModalsProvider>
    </MantineProvider>
  );
}
